import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { isGivmoError, sdk } from '@givmo/api-client';
import { Button, Icon, toast } from '@givmo/design-system';
import { Images, ImagePlus } from 'lucide-react';
import {
  COLLATERAL_COVER_COPY,
  CollateralCoverDialog,
  applyCollateralCover,
  useCoverCollateral,
} from './collateral-cover';
import {
  COVER_ACCEPT,
  COVER_FRAME_CLASS,
  COVER_MAX_BYTES,
  COVER_TOO_LARGE_MESSAGE,
  COVER_WRONG_TYPE_MESSAGE,
  isAcceptableCover,
} from './cover';
import { useFundraisersModule } from './fundraisers-context';
import { useLivePage } from './use-live-page';

/**
 * The existing consumer EDITOR upload, after the org detail has proved scope. `readOnly` (a closed or
 * otherwise frozen row, or no EDITOR rank) shows the cover as it stands — the image, or "No cover
 * photo." — with no upload control; `busy` (another write on the page is running) keeps the control
 * in place, disabled, so the field never flickers between the two.
 */
export function FundraiserCover({
  fundraiserId,
  imageUrl,
  readOnly,
  busy = false,
  onUploaded,
}: {
  fundraiserId: string;
  imageUrl: string | null | undefined;
  readOnly: boolean;
  busy?: boolean;
  onUploaded: () => Promise<unknown>;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const live = useLivePage();
  const disabled = readOnly || busy;
  // The organization's Collateral, offered beside Upload while it holds a current image.
  const { orgRef } = useFundraisersModule();
  const library = useCoverCollateral(readOnly ? null : orgRef);
  const [choosing, setChoosing] = useState(false);
  useEffect(
    () => () => {
      if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview);
    },
    [preview],
  );
  const upload = useMutation({
    mutationFn: async (file: File) => {
      if (disabled) throw new Error('Cover changes are not available.');
      return sdk.fundraisersUploadFundraiserPicture({
        path: { fundraiser_id: fundraiserId },
        body: { file },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      await onUploaded();
      if (!live()) return;
      setPreview(null);
      toast.success('Cover photo updated');
    },
    onError: (caught) => {
      if (!live()) return;
      setPreview(null);
      setError(
        isGivmoError(caught)
          ? caught.message
          : 'Couldn’t upload this cover photo. Please try again.',
      );
    },
  });
  const fromLibrary = useMutation({
    mutationFn: async (itemId: string) => {
      if (disabled || orgRef === null) throw new Error('Cover changes are not available.');
      await applyCollateralCover(orgRef, itemId, fundraiserId);
    },
    onSuccess: async () => {
      await onUploaded();
      if (!live()) return;
      setPreview(null);
      toast.success('Cover photo updated');
    },
    onError: (caught) => {
      if (!live()) return;
      setPreview(null);
      setError(isGivmoError(caught) ? caught.message : COLLATERAL_COVER_COPY.failed);
    },
  });
  const pending = upload.isPending || fromLibrary.isPending;
  function pick(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || disabled || pending) return;
    if (!isAcceptableCover(file)) {
      setError(COVER_WRONG_TYPE_MESSAGE);
      return;
    }
    if (file.size > COVER_MAX_BYTES) {
      setError(COVER_TOO_LARGE_MESSAGE);
      return;
    }
    setError(null);
    setPreview(URL.createObjectURL(file));
    upload.mutate(file);
  }
  const shown = preview ?? imageUrl;
  return (
    <div className="flex flex-col gap-8" data-testid="fundraiser-cover">
      <span className="text-s13 font-w600 text-text-strong">Cover photo</span>
      {shown ? (
        <img src={shown} alt="Fundraiser cover" className={COVER_FRAME_CLASS} />
      ) : readOnly ? (
        <p className="text-s12 text-text-tertiary" data-testid="fundraiser-cover-none">
          No cover photo.
        </p>
      ) : null}
      {!readOnly && (
        <Button
          appearance="saas"
          tone="ghost"
          className="h-auto min-h-[72px] w-full justify-center gap-10 rounded-lg border border-dashed border-border px-20 py-24 text-text-secondary font-w400"
          onClick={() => input.current?.click()}
          disabled={busy || pending}
          leadingIcon={<Icon icon={ImagePlus} size={20} aria-hidden />}
          testId="fundraiser-cover-pick"
        >
          {pending ? 'Uploading cover photo…' : shown ? 'Replace cover photo' : 'Add a cover photo'}
        </Button>
      )}
      {!readOnly && library !== null && (
        <>
          <Button
            appearance="saas"
            tone="secondary"
            className="self-start"
            onClick={() => setChoosing(true)}
            disabled={busy || pending}
            leadingIcon={<Icon icon={Images} size={16} aria-hidden />}
            testId="fundraiser-cover-collateral"
          >
            {COLLATERAL_COVER_COPY.choose}
          </Button>
          <CollateralCoverDialog
            open={choosing}
            onOpenChange={setChoosing}
            items={library}
            onChoose={(item) => {
              setChoosing(false);
              setError(null);
              setPreview(item.url);
              fromLibrary.mutate(item.id);
            }}
          />
        </>
      )}
      <input
        ref={input}
        type="file"
        accept={COVER_ACCEPT}
        className="hidden"
        disabled={disabled || pending}
        onChange={pick}
        data-testid="fundraiser-cover-input"
      />
      {error && (
        <p role="alert" className="text-s13 text-danger-700" data-testid="fundraiser-cover-error">
          {error}
        </p>
      )}
    </div>
  );
}
